import React, { useState, useMemo } from 'react';
import { Row, Col, Card, Badge, Accordion, Table, Form, InputGroup, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './HelpGuide.css';

export default function HelpGuide() {
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'circulation' | 'cataloguing' | 'journals' | 'barcode' | 'opac' | 'patron' | 'faq'
  const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'staff' | 'patron'
  const [searchQuery, setSearchQuery] = useState('');

  // Category navigation items
  const categories = [
    { id: 'all', label: 'All Library Guides', icon: 'bi-grid-fill', count: 7 },
    { id: 'circulation', label: '1. Issue & Return Desk', icon: 'bi-arrow-left-right', count: 3, role: 'staff' },
    { id: 'cataloguing', label: '2. Book Cataloguing & Indexing', icon: 'bi-book-half', count: 4, role: 'staff' },
    { id: 'journals', label: '3. Journals & Periodicals', icon: 'bi-journal-album', count: 2, role: 'staff' },
    { id: 'barcode', label: '4. Barcode Print Studio', icon: 'bi-upc-scan', count: 2, role: 'staff' },
    { id: 'opac', label: '5. Search & OPAC Discovery', icon: 'bi-search', count: 3, role: 'patron' },
    { id: 'patron', label: '6. Quotas, Fines & Accounts', icon: 'bi-person-badge', count: 3, role: 'patron' },
    { id: 'faq', label: '7. Frequently Asked Questions', icon: 'bi-question-diamond', count: 5, role: 'all' }
  ];

  const handlePrint = () => {
    window.print();
  };

  const isVisible = (catId, role = 'staff', text = '') => {
    // Check Category tab
    if (activeTab !== 'all' && activeTab !== catId) return false;
    // Check Role filter
    if (roleFilter !== 'all' && role !== 'all' && roleFilter !== role) return false;
    // Check Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!text.toLowerCase().includes(q)) return false;
    }
    return true;
  };

  return (
    <div className="helpguide-container">
      <Helmet>
        <title>Help & User Guide - Standard Operating Procedures | Flynn - Meagher Library</title>
        <meta
          name="description"
          content="Step-by-step user manual and standard operating procedures for library staff, students, and faculty."
        />
      </Helmet>

      <div className="container-fluid px-md-4">
        {/* ================= HERO BANNER ================= */}
        <div className="helpguide-hero">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-white bg-opacity-20 text-white small fw-bold mb-3">
                <i className="bi bi-mortarboard-fill"></i> Flynn - Meagher Library Knowledge Base
              </div>
              <h1 className="helpguide-hero-title">
                Library User Manual & Standard Operating Procedures
              </h1>
              <p className="helpguide-hero-subtitle">
                Clear, step-by-step instructions for circulation, cataloguing, journal indexing, barcode generation, and OPAC search.
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-light fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                onClick={handlePrint}
                title="Print SOP Document"
              >
                <i className="bi bi-printer"></i> Print Manual
              </button>
            </div>
          </div>

          {/* Search Box */}
          <div className="helpguide-search-box mt-2">
            <i className="bi bi-search text-muted fs-5 ms-2"></i>
            <Form.Control
              type="text"
              className="helpguide-search-input flex-grow-1"
              placeholder="Search workflows (e.g. 'issue book', 'manual accession', 'renew', 'fine', 'ISBN')..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button
                variant="link"
                className="text-muted p-1 me-2"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <i className="bi bi-x-circle-fill"></i>
              </Button>
            )}
          </div>

          {/* Quick Role Switcher */}
          <div className="helpguide-role-pills">
            <span className="text-white text-opacity-75 small fw-semibold align-self-center me-1">Audience:</span>
            <button
              type="button"
              className={`helpguide-role-btn ${roleFilter === 'all' ? 'active' : ''}`}
              onClick={() => setRoleFilter('all')}
            >
              <i className="bi bi-people"></i> All Workflows
            </button>
            <button
              type="button"
              className={`helpguide-role-btn ${roleFilter === 'staff' ? 'active' : ''}`}
              onClick={() => setRoleFilter('staff')}
            >
              <i className="bi bi-shield-lock"></i> Librarian & Staff SOP
            </button>
            <button
              type="button"
              className={`helpguide-role-btn ${roleFilter === 'patron' ? 'active' : ''}`}
              onClick={() => setRoleFilter('patron')}
            >
              <i className="bi bi-person-check"></i> Student & Faculty Guide
            </button>
          </div>
        </div>

        {/* ================= MAIN CONTENT ROW ================= */}
        <Row className="g-4">
          {/* Left Sidebar Navigator */}
          <Col lg={3} md={4} className="d-none d-md-block">
            <div className="helpguide-nav-card">
              <div className="helpguide-nav-title">
                <i className="bi bi-compass me-1"></i> Module Guide
              </div>
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`helpguide-nav-item ${activeTab === cat.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(cat.id)}
                >
                  <span className="d-flex align-items-center gap-2">
                    <i className={`bi ${cat.icon}`}></i>
                    <span>{cat.label}</span>
                  </span>
                  <span className="helpguide-nav-badge">{cat.count}</span>
                </div>
              ))}

              <div className="border-top pt-3 mt-3">
                <div className="small text-muted mb-2 px-2">
                  <i className="bi bi-headset me-1 text-primary"></i> Need additional assistance?
                </div>
                <div className="px-2 small">
                  Contact library administration at <strong>vidyajyotilibrary@gmail.com</strong>
                </div>
              </div>
            </div>
          </Col>

          {/* Right Content Stream */}
          <Col lg={9} md={8}>
            {/* ================= SECTION 1: CIRCULATION DESK ================= */}
            {isVisible('circulation', 'staff', 'Issue Return Desk Circulation Borrower scan student faculty quota fines renewal checkout loan days') && (
              <div className="helpguide-card" id="circulation-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-arrow-left-right text-primary"></i>
                    1. Circulation Desk (Issue, Return & Renew)
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="primary">Staff SOP</Badge>
                    <Link to="/issuereturnbooks" className="btn-helpguide-action">
                      Open Issue Desk <i className="bi bi-box-arrow-up-right"></i>
                    </Link>
                  </div>
                </div>

                <div className="helpguide-card-body">
                  <h5 className="fw-bold text-dark fs-6 mb-3">
                    <i className="bi bi-box-arrow-up-right text-primary me-2"></i>
                    A. How to Issue Books to a Borrower
                  </h5>

                  <div className="helpguide-step-timeline">
                    {/* Step 1 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">1</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-person-badge text-primary"></i> Step 1: Identify Borrower
                        </div>
                        <p className="helpguide-step-desc">
                          In the <strong>Identify Borrower</strong> panel on the left, select your search method:
                        </p>
                        <ul className="small text-muted ps-3 mb-2">
                          <li><strong>User ID (Barcode):</strong> Scan the student card barcode or enter Registration ID (e.g. <code>24BTH01</code>, <code>23LTH05</code>) or Faculty ID (e.g. <code>VJS01</code>).</li>
                          <li><strong>Name Search:</strong> When you choose <em>Name</em> from the dropdown, a toggle appears on the top-right of the box. Select <strong>Student</strong> or <strong>Faculty</strong>, type at least 3 characters, and click Search.</li>
                          <li>If multiple borrowers match the name, a popup modal will appear. Click <strong>Select</strong> on the appropriate row.</li>
                        </ul>
                        <div className="helpguide-callout helpguide-callout-info">
                          <i className="bi bi-info-circle-fill helpguide-callout-icon"></i>
                          <div>
                            Once identified, the borrower’s profile photo, department, allowed loan days, active loans, and visual <strong>Borrowing Quota Bar</strong> will be displayed immediately.
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">2</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-upc-scan text-primary"></i> Step 2: Search & Stage Books
                        </div>
                        <p className="helpguide-step-desc">
                          In the <strong>Stage Books for Checkout</strong> panel on the right:
                        </p>
                        <ul className="small text-muted ps-3 mb-2">
                          <li>Scan the book's barcode or type the Accession Number (or search by Book Title / Author).</li>
                          <li>Click <strong>Add Book</strong>. The book is added to the temporary checkout staging list.</li>
                          <li>You can stage multiple books in a single session for the same borrower.</li>
                        </ul>
                        <div className="helpguide-callout helpguide-callout-warning">
                          <i className="bi bi-exclamation-triangle-fill helpguide-callout-icon"></i>
                          <div>
                            <strong>Automatic Protection Rules:</strong>
                            <ul className="mb-0 ps-3 mt-1">
                              <li>Books with <em>Reference Scope</em> cannot be issued outside the library.</li>
                              <li>Books already marked as <em>Issued</em> or <em>Lost</em> cannot be staged.</li>
                              <li>The system blocks transactions that would exceed the borrower's department quota.</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">3</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-check2-circle text-success"></i> Step 3: Confirm & Issue
                        </div>
                        <p className="helpguide-step-desc">
                          Review the staged books list. Click the green <strong>Confirm & Issue</strong> button.
                        </p>
                        <div className="helpguide-callout helpguide-callout-success">
                          <i className="bi bi-check-circle-fill helpguide-callout-icon"></i>
                          <div>
                            The system computes the return due date automatically based on the borrower’s program, records the transaction, updates book availability to <em>Issued</em>, and sends an email receipt with due dates to the borrower!
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* Section 1B: Returns & Renewals */}
                  <h5 className="fw-bold text-dark fs-6 mb-3">
                    <i className="bi bi-arrow-counterclockwise text-primary me-2"></i>
                    B. How to Return & Renew Books
                  </h5>

                  <div className="helpguide-step-timeline">
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">1</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-search text-primary"></i> Step 1: Locate Active Loan
                        </div>
                        <p className="helpguide-step-desc">
                          Switch to the <strong>Return & Renew</strong> tab. You can search by Book Accession # or Borrower Name/ID, or use the quick filter buttons: <strong>All Loans</strong>, <strong>Due Today</strong>, or <strong>Overdue Loans</strong>.
                        </p>
                      </div>
                    </div>

                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">2</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-calculator text-primary"></i> Step 2: Check Overdue Status & Fine
                        </div>
                        <p className="helpguide-step-desc">
                          The system automatically calculates the days since issue against the category loan allowance.
                          Overdue loans are highlighted in light red, displaying the overdue days and fine amount calculated at <strong>₹1 per overdue day</strong>.
                        </p>
                      </div>
                    </div>

                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">3</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-check2-square text-success"></i> Step 3: Complete Return or Renewal
                        </div>
                        <ul className="small text-muted ps-3 mb-0">
                          <li><strong>Return Book:</strong> Click the green <em>Return</em> button. The loan is closed, and the book status is restored to <em>Available</em>.</li>
                          <li><strong>Renew Loan:</strong> Click the blue <em>Renew</em> button. The loan period is extended from today's date according to the borrower's quota.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= SECTION 2: BOOK CATALOGUING & MANAGEMENT ================= */}
            {isVisible('cataloguing', 'staff', 'Cataloguing New Book Manual Accession ISBN Metadata Fetch Chapters Articles Indexing Clone') && (
              <div className="helpguide-card" id="cataloguing-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-book-half text-primary"></i>
                    2. Book Cataloguing & Management
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="primary">Staff SOP</Badge>
                    <Link to="/managebooks" className="btn-helpguide-action">
                      Manage Books <i className="bi bi-box-arrow-up-right"></i>
                    </Link>
                  </div>
                </div>

                <div className="helpguide-card-body">
                  <h5 className="fw-bold text-dark fs-6 mb-3">
                    <i className="bi bi-plus-circle text-primary me-2"></i>
                    A. Cataloguing a New Book into the Library System
                  </h5>

                  <div className="helpguide-step-timeline">
                    {/* Step 1 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">1</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-toggle2-on text-primary"></i> Step 1: Accession Number Mode
                        </div>
                        <p className="helpguide-step-desc">
                          Click <strong>+ Catalogue New Book</strong>. In the top section:
                        </p>
                        <ul className="small text-muted ps-3 mb-2">
                          <li><strong>Auto-Generate (Default):</strong> The library server will automatically assign the next sequential accession number.</li>
                          <li><strong>Manual Accession Number:</strong> Toggle the switch OFF to enter a custom barcode or accession number.</li>
                        </ul>
                        <div className="helpguide-callout helpguide-callout-info">
                          <i className="bi bi-shield-check helpguide-callout-icon"></i>
                          <div>
                            <strong>Real-time 6-Character Verification:</strong> When entering a manual accession number, as soon as you type 6 or more characters, the system validates against the database in the background to ensure it is unique and available.
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">2</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-lightning-charge text-primary"></i> Step 2: Fast-Track ISBN Metadata Fetch (Optional)
                        </div>
                        <p className="helpguide-step-desc">
                          Enter the 10-digit or 13-digit ISBN in the ISBN field and click <strong>Fetch Metadata</strong>. The system will search global bibliographic databases and auto-populate Book Title, Subtitle, Author, Publisher Name, Publish Year, Page Count, and Abstract!
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">3</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-tags text-primary"></i> Step 3: Classification, Location & Keywords
                        </div>
                        <ul className="small text-muted ps-3 mb-2">
                          <li><strong>Category & Call No:</strong> Select the Subject Category (e.g. Dogmatic Theology, Scripture, Philosophy). The Call Number / Classification No is assigned accordingly.</li>
                          <li><strong>Author Mark:</strong> Enter the 3-letter/number Cutter author mark.</li>
                          <li><strong>Shelf Location:</strong> Specify stack location (e.g. <em>Ground Floor Main Stack, Shelf 4B</em>).</li>
                          <li><strong>Keywords:</strong> Add comma-separated subject keywords (up to 10 keywords) to enhance searchability.</li>
                          <li><strong>Cover Image:</strong> Upload the book cover/jacket image (stored on library cloud storage).</li>
                        </ul>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">4</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-eye text-primary"></i> Step 4: Verification Screen & Saving
                        </div>
                        <p className="helpguide-step-desc">
                          Click <strong>Preview & Verify</strong> to open the formatted bibliographic summary sheet.
                        </p>
                        <ul className="small text-muted ps-3 mb-2">
                          <li>If you need to make changes, click <strong>← Back to Edit Details</strong>.</li>
                          <li>If everything is verified, click <strong>Save & Catalogue</strong> to finalize.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* Section 2B: Chapter Indexing */}
                  <h5 className="fw-bold text-dark fs-6 mb-3">
                    <i className="bi bi-journal-text text-primary me-2"></i>
                    B. Indexing Book Chapters & Anthology Articles
                  </h5>
                  <p className="small text-muted">
                    For edited volumes, collected essays, and conference proceedings, you can index each individual chapter with its author so researchers can find individual articles:
                  </p>
                  <ol className="small text-muted ps-3 mb-0">
                    <li>In the Manage Books table, find the volume and click the <strong>Articles (<i className="bi bi-journal-text text-primary"></i>)</strong> button.</li>
                    <li>Click <strong>+ Add Article / Chapter</strong>.</li>
                    <li>Enter Chapter Title, Contributing Author(s), Page Range (e.g., <code>pp. 45–68</code>), and Abstract.</li>
                    <li>Save. The chapter is now searchable directly in the OPAC search portal!</li>
                  </ol>
                </div>
              </div>
            )}

            {/* ================= SECTION 3: JOURNALS & PERIODICALS ================= */}
            {isVisible('journals', 'staff', 'Journals Periodicals Issues Volume ISSN Articles') && (
              <div className="helpguide-card" id="journals-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-journal-album text-primary"></i>
                    3. Journals & Periodicals Management
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="primary">Staff SOP</Badge>
                    <Link to="/managejournal" className="btn-helpguide-action">
                      Manage Journals <i className="bi bi-box-arrow-up-right"></i>
                    </Link>
                  </div>
                </div>

                <div className="helpguide-card-body">
                  <p className="small text-muted">
                    Flynn - Meagher Library manages subscribed theological, philosophical, and biblical journals through the Journal Management Studio:
                  </p>
                  <div className="helpguide-step-timeline">
                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">1</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-plus-square text-primary"></i> Step 1: Register Journal Title
                        </div>
                        <p className="helpguide-step-desc">
                          In <strong>Manage Journals</strong>, create a master record with Journal Title, Publisher, ISSN, Frequency (Monthly, Quarterly, Bi-Annual), and Shelf Location.
                        </p>
                      </div>
                    </div>

                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">2</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-collection text-primary"></i> Step 2: Record Volumes & Issues
                        </div>
                        <p className="helpguide-step-desc">
                          Under each journal, record incoming physical issues with Year, Volume Number, Issue Number, and Month/Season.
                        </p>
                      </div>
                    </div>

                    <div className="helpguide-step-item">
                      <div className="helpguide-step-bubble">3</div>
                      <div className="helpguide-step-content">
                        <div className="helpguide-step-title">
                          <i className="bi bi-file-earmark-text text-primary"></i> Step 3: Index Scholarly Articles
                        </div>
                        <p className="helpguide-step-desc">
                          Index individual journal articles with Title, Author, Abstract, Keywords, and page references so scholars can search and retrieve specific theological research papers.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= SECTION 4: BARCODE PRINT STUDIO ================= */}
            {isVisible('barcode', 'staff', 'Barcode Print Studio Labels Spine Barcodes Code 128') && (
              <div className="helpguide-card" id="barcode-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-upc-scan text-primary"></i>
                    4. Barcode Print Studio & Spine Labeling
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="primary">Staff SOP</Badge>
                    <Link to="/barcode" className="btn-helpguide-action">
                      Barcode Studio <i className="bi bi-box-arrow-up-right"></i>
                    </Link>
                  </div>
                </div>

                <div className="helpguide-card-body">
                  <p className="small text-muted">
                    Generate and print crisp Code-128 barcode labels formatted for standard sticker label sheets:
                  </p>
                  <ol className="small text-muted ps-3 mb-2">
                    <li>Navigate to the <strong>Barcode Studio</strong>.</li>
                    <li>Select books by Accession Number range, Category, or recent cataloguing batch.</li>
                    <li>Preview the formatted labels showing: <strong>Accession # Barcode</strong>, <strong>Call Number</strong>, and <strong>Library Name</strong>.</li>
                    <li>Click <strong>Print Barcodes</strong> to print directly to label printers or A4 sheet stickers.</li>
                  </ol>
                </div>
              </div>
            )}

            {/* ================= SECTION 5: OPAC & CATALOGUE DISCOVERY ================= */}
            {isVisible('opac', 'all', 'OPAC Search Books Articles Call Number Shelf Stacks Keywords') && (
              <div className="helpguide-card" id="opac-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-search text-primary"></i>
                    5. Online Public Access Catalogue (OPAC) & Discovery
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="success">Students & Faculty</Badge>
                    <Link to="/search" className="btn-helpguide-action">
                      Search OPAC <i className="bi bi-box-arrow-up-right"></i>
                    </Link>
                  </div>
                </div>

                <div className="helpguide-card-body">
                  <h5 className="fw-bold text-dark fs-6 mb-3">
                    <i className="bi bi-funnel text-primary me-2"></i>
                    Search Tips for Researchers and Students
                  </h5>

                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <div className="p-3 bg-light rounded-3 border h-100">
                        <div className="fw-bold text-dark small mb-1">
                          <i className="bi bi-book text-primary me-1"></i> Book Title & Author Search
                        </div>
                        <div className="small text-muted">
                          Search by exact title keywords or author surname (e.g. <em>Ratzinger</em>, <em>Rahner</em>, <em>Aquinas</em>).
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-light rounded-3 border h-100">
                        <div className="fw-bold text-dark small mb-1">
                          <i className="bi bi-journal-text text-primary me-1"></i> Chapter & Article Search
                        </div>
                        <div className="small text-muted">
                          Switch topic to <strong>Article Title</strong> or <strong>Article Keywords</strong> to find indexed chapters in edited volumes and journals.
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-light rounded-3 border h-100">
                        <div className="fw-bold text-dark small mb-1">
                          <i className="bi bi-bookmark text-primary me-1"></i> Call Number & Classification
                        </div>
                        <div className="small text-muted">
                          Use the 3-digit Dewey / Theological classification (e.g. <code>230</code> for Dogmatics, <code>220</code> for Bible) to browse subjects.
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-light rounded-3 border h-100">
                        <div className="fw-bold text-dark small mb-1">
                          <i className="bi bi-geo-alt text-primary me-1"></i> Shelf Availability & Location
                        </div>
                        <div className="small text-muted">
                          Each book card displays real-time status (<strong>Available</strong> vs <strong>Issued</strong>) and the exact shelf location in the stacks.
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* ================= SECTION 6: BORROWING QUOTAS & POLICIES ================= */}
            {isVisible('patron', 'all', 'Borrowing Quota Loan Days Fines Limits Overdue Rules Policy BTH LTH DTH OYT Faculty') && (
              <div className="helpguide-card" id="patron-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-person-badge text-primary"></i>
                    6. Borrowing Quotas, Loan Periods & Fines Policy
                  </h3>
                  <Badge bg="info" className="text-dark">Official Policy</Badge>
                </div>

                <div className="helpguide-card-body">
                  <p className="small text-muted mb-3">
                    Borrowing quotas and loan periods are automatically configured by student program and staff category:
                  </p>

                  <div className="table-responsive mb-3">
                    <Table className="helpguide-table align-middle">
                      <thead>
                        <tr>
                          <th>Borrower Category / Degree Program</th>
                          <th>Department Code</th>
                          <th className="text-center">Maximum Books Quota</th>
                          <th className="text-center">Loan Period</th>
                          <th className="text-center">Daily Overdue Fine</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Faculty & Teaching Staff</strong></td>
                          <td><Badge bg="secondary">VJS / Faculty</Badge></td>
                          <td className="text-center fw-bold text-primary">50 Books</td>
                          <td className="text-center fw-bold">90 Days</td>
                          <td className="text-center text-muted">Exempt</td>
                        </tr>
                        <tr>
                          <td><strong>Doctorate in Theology (DTH / Ph.D)</strong></td>
                          <td><Badge bg="light" className="text-dark border">DTH</Badge></td>
                          <td className="text-center fw-bold text-primary">50 Books</td>
                          <td className="text-center fw-bold">60 Days</td>
                          <td className="text-center text-danger fw-semibold">₹1 / Day</td>
                        </tr>
                        <tr>
                          <td><strong>Licentiate in Sacred Theology (LTH / M.Th)</strong></td>
                          <td><Badge bg="light" className="text-dark border">LTH</Badge></td>
                          <td className="text-center fw-bold text-primary">30 Books</td>
                          <td className="text-center fw-bold">40 Days</td>
                          <td className="text-center text-danger fw-semibold">₹1 / Day</td>
                        </tr>
                        <tr>
                          <td><strong>Bachelor in Theology (BTH)</strong></td>
                          <td><Badge bg="light" className="text-dark border">BTH</Badge></td>
                          <td className="text-center fw-bold text-primary">20 Books</td>
                          <td className="text-center fw-bold">30 Days</td>
                          <td className="text-center text-danger fw-semibold">₹1 / Day</td>
                        </tr>
                        <tr>
                          <td><strong>One Year Theology (OYT) / Diploma (RTD)</strong></td>
                          <td><Badge bg="light" className="text-dark border">OYT / RTD</Badge></td>
                          <td className="text-center fw-bold text-primary">20 Books</td>
                          <td className="text-center fw-bold">30 Days</td>
                          <td className="text-center text-danger fw-semibold">₹1 / Day</td>
                        </tr>
                        <tr>
                          <td><strong>General / Other Registered Students</strong></td>
                          <td><Badge bg="light" className="text-dark border">General</Badge></td>
                          <td className="text-center fw-bold text-primary">20 Books</td>
                          <td className="text-center fw-bold">30 Days</td>
                          <td className="text-center text-danger fw-semibold">₹1 / Day</td>
                        </tr>
                        <tr className="table-warning bg-opacity-25">
                          <td><strong>Reference Books (Students)</strong></td>
                          <td><Badge bg="warning" text="dark">All Students</Badge></td>
                          <td className="text-center fw-bold text-dark">Within Quota</td>
                          <td className="text-center fw-bold text-danger">1 Day</td>
                          <td className="text-center text-danger fw-bold">₹1 / Day (post 1 day)</td>
                        </tr>
                        <tr className="table-info bg-opacity-25">
                          <td><strong>Reference Books (Faculty / Staff)</strong></td>
                          <td><Badge bg="secondary">VJS / Faculty</Badge></td>
                          <td className="text-center fw-bold text-primary">50 Books</td>
                          <td className="text-center fw-bold">90 Days</td>
                          <td className="text-center text-muted">Exempt</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>

                  <div className="helpguide-callout helpguide-callout-warning">
                    <i className="bi bi-clock-history helpguide-callout-icon"></i>
                    <div>
                      <strong>Reference Books Loan Policy:</strong> Reference books (marked with <em>Reference</em> scope) can be checked out by registered students for a <strong>1-day loan period</strong>. If kept beyond 1 day, standard overdue fines (₹1/day) apply. Faculty and staff enjoy the standard <strong>90-day loan period</strong> for reference items.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= SECTION 7: FAQ ================= */}
            {isVisible('faq', 'all', 'FAQ Questions Lost Card Reference Fines Renewal') && (
              <div className="helpguide-card" id="faq-section">
                <div className="helpguide-card-header">
                  <h3 className="helpguide-card-title">
                    <i className="bi bi-question-diamond text-primary"></i>
                    7. Frequently Asked Questions (FAQ)
                  </h3>
                </div>

                <div className="helpguide-card-body">
                  <Accordion defaultActiveKey="0" flush>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <strong>Q: Can Reference books be issued outside the library?</strong>
                      </Accordion.Header>
                      <Accordion.Body className="small text-muted">
                        Yes! Reference books can be issued to students with a <strong>1-day loan period</strong>. Post 1 day, standard overdue fines of ₹1 per day will be charged upon return. Faculty and teaching staff have a <strong>90-day loan period</strong> for reference books.
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        <strong>Q: How can a student check their active borrowed books and due dates?</strong>
                      </Accordion.Header>
                      <Accordion.Body className="small text-muted">
                        Students can log in with their college account, click their name in the top navigation bar, and select <strong>My Borrowed Books</strong> (<Link to="/mybooks">/mybooks</Link>) to view all currently checked out titles, issue dates, and return due dates.
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="2">
                      <Accordion.Header>
                        <strong>Q: What happens if a borrower loses their library card?</strong>
                      </Accordion.Header>
                      <Accordion.Body className="small text-muted">
                        The librarian can issue books using the borrower's registered Name or Registration Number (e.g. <code>24BTH01</code>). Lost cards should be reported to the circulation desk immediately to prevent unauthorized checkouts.
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="3">
                      <Accordion.Header>
                        <strong>Q: How do I catalog multiple copies of the same book title?</strong>
                      </Accordion.Header>
                      <Accordion.Body className="small text-muted">
                        When cataloguing with <em>Auto-generate Accession Number</em> turned ON, you can specify the number of copies (e.g. <code>3 Copies</code>). The system will automatically create 3 distinct sequential accession numbers for each physical copy!
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="4">
                      <Accordion.Header>
                        <strong>Q: How are overdue fines calculated and settled?</strong>
                      </Accordion.Header>
                      <Accordion.Body className="small text-muted">
                        Overdue fines accrue at ₹1 per overdue day past the official due date. Fines can be settled directly with the circulation desk librarian upon book return.
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}
